import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  AttachMoney as MoneyIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  HourglassEmpty as HourglassIcon,
  LocalShipping as ShipmentIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import {
  AccountDto,
  accountService,
  ShipmentDto,
  ShipmentStatisticsDto,
  ShipmentStatus,
  ShipmentTimelineEvent,
  shipmentService,
} from '../api/adminServices'
import { formatCurrency } from '../utils/formatCurrency'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { exportCsv } from '../utils/exportCsv'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import ConfirmDialog from '../components/ConfirmDialog'
import { PropertyView, PropertySection, PropertyField } from '../components/PropertyView'
import { fmtDateTime } from '../utils/propertyFormat'

type TabKey = 'statistics' | 'shipments' | 'pending'

const PAGE_SIZE = 20

const STATUS_OPTIONS: ShipmentStatus[] = [
  'PENDING_CONFIRMATION',
  'PENDING_MATCHING',
  'PLANNED',
  'PICKED_UP',
  'IN_TRANSIT',
  'DELIVERED',
  'LODGE',
  'PROBLEM',
  'NO_SHOW',
  'CANCELLED',
  'PENDING_SUPPLEMENT',
]

function statusColor(
  s?: string
): 'default' | 'warning' | 'info' | 'success' | 'error' {
  if (!s) return 'default'
  if (s === 'DELIVERED') return 'success'
  if (s === 'CANCELLED' || s === 'PROBLEM' || s === 'NO_SHOW') return 'error'
  if (s === 'IN_TRANSIT' || s === 'PICKED_UP' || s === 'PLANNED') return 'info'
  if (s === 'PENDING_CONFIRMATION' || s === 'PENDING_MATCHING' || s === 'PENDING_SUPPLEMENT' || s === 'LODGE')
    return 'warning'
  return 'default'
}

function cargoSummary(s: ShipmentDto): string {
  if (!s.cargos || s.cargos.length === 0) return '—'
  const addrs = s.cargos.map((c) => c.deliveryAddress).filter(Boolean) as string[]
  if (addrs.length === 0) return `${s.cargos.length} cargo(s)`
  if (addrs.length === 1) return addrs[0]
  return `${addrs[0]} +${addrs.length - 1} more`
}

function StatisticsTab() {
  const theme = useTheme()
  const {
    data: statistics,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['shipment-statistics'],
    queryFn: () => shipmentService.getStatistics(),
    retry: 1,
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton onClick={() => refetch()} disabled={isRefetching} size="small">
            <RefreshIcon />
          </IconButton>
        }
      >
        Failed to load shipment statistics. {(error as Error).message}
      </Alert>
    )
  }

  const stats = (statistics || {}) as ShipmentStatisticsDto
  const total = stats.totalShipments ?? 0
  const pct = (n?: number) =>
    total === 0 ? 0 : Math.min(100, Math.round(((n ?? 0) / total) * 100))

  const tiles = [
    {
      label: 'Total',
      value: total.toLocaleString(),
      icon: <ShipmentIcon />,
      color: theme.palette.primary.main,
    },
    {
      label: 'Pending',
      value: (stats.pendingShipments ?? 0).toLocaleString(),
      icon: <HourglassIcon />,
      color: theme.palette.warning.main,
    },
    {
      label: 'In transit',
      value: (stats.inTransitShipments ?? 0).toLocaleString(),
      icon: <ScheduleIcon />,
      color: theme.palette.info.main,
    },
    {
      label: 'Delivered',
      value: (stats.deliveredShipments ?? stats.completedShipments ?? 0).toLocaleString(),
      icon: <CheckCircleIcon />,
      color: theme.palette.success.main,
    },
    {
      label: 'Cancelled',
      value: (stats.cancelledShipments ?? 0).toLocaleString(),
      icon: <CancelIcon />,
      color: theme.palette.error.main,
    },
    {
      label: 'Revenue',
      value: stats.totalRevenue != null ? formatCurrency(stats.totalRevenue) : '—',
      icon: <MoneyIcon />,
      color: theme.palette.success.main,
    },
  ]

  return (
    <Box>
      <Grid container spacing={2}>
        {tiles.map((t) => (
          <Grid item xs={6} md={4} lg={2} key={t.label}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(t.color, 0.12),
                      color: t.color,
                    }}
                  >
                    {t.icon}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {t.value}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {total > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Status Distribution</Typography>
              <Typography variant="caption" color="text.secondary">
                Percentage of {total.toLocaleString()} total shipments
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              {[
                { key: 'Pending', value: stats.pendingShipments, color: 'warning' as const },
                { key: 'In Transit', value: stats.inTransitShipments, color: 'info' as const },
                {
                  key: 'Delivered',
                  value: stats.deliveredShipments ?? stats.completedShipments,
                  color: 'success' as const,
                },
                { key: 'Cancelled', value: stats.cancelledShipments, color: 'error' as const },
              ].map(({ key, value, color }) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">{key}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {(value ?? 0).toLocaleString()} ({pct(value)}%)
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct(value)}
                    sx={{ height: 8, borderRadius: 4 }}
                    color={color}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

interface ShipmentRowsProps {
  rows: ShipmentDto[]
  totalPages: number
  page: number
  onPageChange: (p: number) => void
  onSelect: (s: ShipmentDto) => void
  accountById: Map<string, AccountDto>
  loading: boolean
}

function ShipmentRows({
  rows,
  totalPages,
  page,
  onPageChange,
  onSelect,
  accountById,
  loading,
}: ShipmentRowsProps) {
  const getLocalizedLabel = useLocalizedLabel()

  if (loading) {
    return (
      <Paper sx={{ flex: 1, minHeight: 0 }}>
        <TableSkeleton rows={8} columns={9} />
      </Paper>
    )
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table size="small" sx={{ height: rows.length === 0 ? '100%' : undefined }}>
          <TableHead>
            <TableRow>
              <ResizableTableCell>Status</ResizableTableCell>
              <ResizableTableCell>Shipper</ResizableTableCell>
              <ResizableTableCell>Freighter</ResizableTableCell>
              <ResizableTableCell>Pickup</ResizableTableCell>
              <ResizableTableCell>Delivery</ResizableTableCell>
              <ResizableTableCell>Pickup Window</ResizableTableCell>
              <ResizableTableCell>Price</ResizableTableCell>
              <ResizableTableCell>Created</ResizableTableCell>
              <ResizableTableCell width={60} sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                  <EmptyState
                    title="No shipments match the current filters"
                    description="Adjust the status, account, date, or search filters above."
                  />
                </TableCell>
              </TableRow>
            )}
            {rows.map((s) => {
              const shipper = s.shipperAccountId ? accountById.get(s.shipperAccountId) : undefined
              const freighter = s.freighterAccountId
                ? accountById.get(s.freighterAccountId)
                : undefined
              return (
                <TableRow
                  key={s.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onSelect(s)}
                >
                  <TableCell>
                    <Chip
                      label={getLocalizedLabel('shipmentStatus', s.status) || s.status}
                      color={statusColor(s.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={s.shipperAccountId || ''}>
                      <span>
                        {shipper?.businessName ||
                          (s.shipperAccountId?.substring(0, 10) + '…' || '—')}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={s.freighterAccountId || ''}>
                      <span>
                        {freighter?.businessName ||
                          (s.freighterAccountId?.substring(0, 10) + '…' || '—')}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 180,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.pickupAddress || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 180,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cargoSummary(s)}
                  </TableCell>
                  <TableCell>
                    {s.pickupWindowStart
                      ? new Date(s.pickupWindowStart).toLocaleString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {s.offeredPrice != null
                      ? `${formatCurrency(s.offeredPrice)} ${s.currency || ''}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                    <IconButton size="small" onClick={() => onSelect(s)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_e, v) => onPageChange(v - 1)}
            color="primary"
          />
        </Box>
      )}
    </>
  )
}

interface ShipmentDetailDialogProps {
  open: boolean
  shipment: ShipmentDto | null
  onClose: () => void
}

function ShipmentDetailDialog({ open, shipment, onClose }: ShipmentDetailDialogProps) {
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()
  const toast = useToast()
  const [statusDraft, setStatusDraft] = useState<string>('')
  const [statusReason, setStatusReason] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)

  const { data: timeline } = useQuery({
    queryKey: ['shipment-timeline', shipment?.id],
    queryFn: () => shipmentService.getTimeline(shipment!.id),
    enabled: open && !!shipment?.id,
  })

  const updateStatus = useMutation({
    mutationFn: () =>
      shipmentService.updateStatus(shipment!.id, statusDraft, statusReason || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shipments'] })
      queryClient.invalidateQueries({ queryKey: ['admin-pending-matching'] })
      queryClient.invalidateQueries({ queryKey: ['shipment-timeline', shipment?.id] })
      setStatusDraft('')
      setStatusReason('')
      toast.success('Shipment status updated')
    },
    onError: (e) => toast.errorFrom(e, 'Status update failed'),
  })

  const cancelShipment = useMutation({
    mutationFn: () => shipmentService.cancel(shipment!.id, cancelReason || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shipments'] })
      queryClient.invalidateQueries({ queryKey: ['admin-pending-matching'] })
      setCancelReason('')
      setCancelConfirmOpen(false)
      toast.success('Shipment cancelled')
      onClose()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to cancel shipment'),
  })

  if (!shipment) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Shipment Details
        <Typography variant="caption" color="text.secondary" display="block">
          {shipment.id}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Chip
              label={getLocalizedLabel('shipmentStatus', shipment.status) || shipment.status}
              color={statusColor(shipment.status)}
              size="small"
            />
            {shipment.urgency && (
              <Chip
                label={getLocalizedLabel('shipmentUrgency', shipment.urgency) || shipment.urgency}
                size="small"
                variant="outlined"
              />
            )}
            {shipment.jobId && (
              <Chip label={`Job ${shipment.jobId.substring(0, 8)}…`} size="small" variant="outlined" />
            )}
          </Stack>

          <Divider />

          <PropertyView>
            <PropertySection title="Parties">
              <PropertyField label="Shipper Account" value={shipment.shipperAccountId} mono copyable />
              <PropertyField label="Freighter Account" value={shipment.freighterAccountId} mono copyable />
              {shipment.assignedDriver && (
                <PropertyField
                  label="Assigned Driver"
                  value={
                    shipment.assignedDriver.name
                      ? `${shipment.assignedDriver.name}${shipment.assignedDriver.phone ? ` · ${shipment.assignedDriver.phone}` : ''}`
                      : null
                  }
                  full
                />
              )}
            </PropertySection>
            <PropertySection title="Pickup">
              <PropertyField label="Pickup Address" full>
                <Box>
                  <Typography variant="body2">{shipment.pickupAddress || '—'}</Typography>
                  {(shipment.pickupContactName || shipment.pickupContactPhone) && (
                    <Typography variant="caption" color="text.secondary">
                      {shipment.pickupContactName || ''}
                      {shipment.pickupContactPhone ? ` · ${shipment.pickupContactPhone}` : ''}
                    </Typography>
                  )}
                </Box>
              </PropertyField>
              <PropertyField label="Pickup Window" full>
                <Typography variant="body2">
                  {shipment.pickupWindowStart ? fmtDateTime(shipment.pickupWindowStart) : '—'}
                  {shipment.pickupWindowEnd ? ` → ${fmtDateTime(shipment.pickupWindowEnd)}` : ''}
                </Typography>
              </PropertyField>
            </PropertySection>
            <PropertySection title="Logistics" columns={3}>
              <PropertyField
                label="Distance"
                value={shipment.estimatedDistanceKm != null ? `${shipment.estimatedDistanceKm} km` : null}
              />
              <PropertyField
                label="Duration"
                value={
                  shipment.estimatedDurationMinutes != null
                    ? `${shipment.estimatedDurationMinutes} min`
                    : null
                }
              />
              <PropertyField
                label="Weight"
                value={
                  shipment.totalWeightKg != null
                    ? `${shipment.totalWeightKg.toLocaleString()} kg`
                    : null
                }
              />
              <PropertyField
                label="Price"
                value={
                  shipment.offeredPrice != null
                    ? `${formatCurrency(shipment.offeredPrice)} ${shipment.currency || ''}`.trim()
                    : null
                }
              />
            </PropertySection>
            {shipment.specialInstructions && (
              <PropertySection title="Notes">
                <PropertyField label="Special Instructions" full>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {shipment.specialInstructions}
                  </Typography>
                </PropertyField>
              </PropertySection>
            )}
          </PropertyView>

          {shipment.cargos && shipment.cargos.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">Cargos ({shipment.cargos.length})</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Delivery Address</TableCell>
                      <TableCell>Receiver</TableCell>
                      <TableCell>Weight</TableCell>
                      <TableCell>Volume</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shipment.cargos.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.deliveryAddress || '—'}</TableCell>
                        <TableCell>
                          {c.receiverName || '—'}
                          {c.receiverPhone ? ` · ${c.receiverPhone}` : ''}
                        </TableCell>
                        <TableCell>
                          {c.weightKg != null ? `${c.weightKg} kg` : '—'}
                        </TableCell>
                        <TableCell>
                          {c.volumeM3 != null ? `${c.volumeM3} m³` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Divider />
          <Typography variant="subtitle2">Timeline</Typography>
          {timeline && timeline.length > 0 ? (
            <Stack spacing={0.5}>
              {(timeline as ShipmentTimelineEvent[]).map((ev, i) => (
                <Stack
                  key={`${ev.type}-${i}`}
                  direction="row"
                  spacing={2}
                  alignItems="baseline"
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 160, fontFamily: 'monospace' }}
                  >
                    {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}
                  </Typography>
                  <Chip label={ev.type} size="small" variant="outlined" />
                  <Typography variant="body2">{ev.label}</Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No timeline events recorded yet.
            </Typography>
          )}

          <Divider />
          <Typography variant="subtitle2">Admin Status Override</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              size="small"
              label="New status"
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">— select —</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {getLocalizedLabel('shipmentStatus', s) || s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Reason (audit)"
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon />}
              disabled={!statusDraft || updateStatus.isPending}
              onClick={() => updateStatus.mutate()}
            >
              Apply
            </Button>
          </Stack>

          {shipment.status !== 'CANCELLED' &&
            shipment.status !== 'DELIVERED' &&
            shipment.status !== 'IN_TRANSIT' &&
            shipment.status !== 'PICKED_UP' && (
              <>
                <Divider />
                <Typography variant="subtitle2" color="error.main">
                  Cancel Shipment
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    size="small"
                    label="Cancellation reason"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => setCancelConfirmOpen(true)}
                    disabled={cancelShipment.isPending}
                  >
                    Cancel Shipment…
                  </Button>
                </Stack>
              </>
            )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Cancel shipment?"
        description={
          <>
            Cancelling this shipment cascades to its job offers and job. This cannot be
            undone.
            {cancelReason ? (
              <>
                {' '}Reason: <strong>{cancelReason}</strong>
              </>
            ) : null}
          </>
        }
        confirmPhrase="CANCEL"
        confirmHelper="Type CANCEL to confirm."
        confirmLabel="Cancel Shipment"
        severity="error"
        loading={cancelShipment.isPending}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => cancelShipment.mutate()}
      />
    </Dialog>
  )
}

function ShipmentListTab() {
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [shipperFilter, setShipperFilter] = useState('')
  const [freighterFilter, setFreighterFilter] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')

  const [selected, setSelected] = useState<ShipmentDto | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const getLocalizedLabel = useLocalizedLabel()
  const queryClient = useQueryClient()

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-filter'],
    queryFn: () => accountService.getAll(0, 200),
  })
  const accounts: AccountDto[] = useMemo(
    () => accountsData?.content ?? accountsData ?? [],
    [accountsData]
  )
  const accountById = useMemo(() => {
    const m = new Map<string, AccountDto>()
    accounts.forEach((a) => m.set(a.id, a))
    return m
  }, [accounts])

  const queryParams = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      status: statusFilter || undefined,
      shipperAccountId: shipperFilter || undefined,
      freighterAccountId: freighterFilter || undefined,
      from: from ? `${from}T00:00:00` : undefined,
      to: to ? `${to}T23:59:59` : undefined,
    }),
    [page, statusFilter, shipperFilter, freighterFilter, from, to]
  )

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-shipments', queryParams],
    queryFn: () => shipmentService.getAll(queryParams),
  })

  const rows: ShipmentDto[] = useMemo(() => data?.content ?? [], [data])
  const totalPages: number = data?.totalPages ?? 0

  const visibleRows = useMemo(() => {
    if (!search) return rows
    const q = search.toLowerCase()
    return rows.filter((r) => {
      const hay = [
        r.id,
        r.pickupAddress,
        r.pickupContactName,
        r.shipmentRequestId,
        r.jobId,
        ...((r.cargos || []).map((c) => c.deliveryAddress).filter(Boolean) as string[]),
      ]
      return hay.some((v) => v?.toLowerCase().includes(q))
    })
  }, [rows, search])

  const handleExport = () => {
    exportCsv<ShipmentDto>(`shipments-${new Date().toISOString().slice(0, 10)}`,
      [
        { header: 'ID', field: 'id' },
        { header: 'Status', field: 'status' },
        { header: 'Shipper', field: 'shipperAccountId' },
        { header: 'Freighter', field: 'freighterAccountId' },
        { header: 'Pickup', field: 'pickupAddress' },
        { header: 'Delivery', field: (r) => cargoSummary(r) },
        { header: 'Pickup Window Start', field: 'pickupWindowStart' },
        { header: 'Pickup Window End', field: 'pickupWindowEnd' },
        { header: 'Distance (km)', field: 'estimatedDistanceKm' },
        { header: 'Weight (kg)', field: 'totalWeightKg' },
        { header: 'Offered Price', field: 'offeredPrice' },
        { header: 'Currency', field: 'currency' },
        { header: 'Created', field: 'createdAt' },
      ],
      visibleRows)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {getLocalizedLabel('shipmentStatus', s) || s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Shipper"
            value={shipperFilter}
            onChange={(e) => {
              setShipperFilter(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All shippers</MenuItem>
            {accounts
              .filter((a) => a.type === 'SHIPPER')
              .map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.businessName || a.id.substring(0, 8)}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Freighter"
            value={freighterFilter}
            onChange={(e) => {
              setFreighterFilter(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All freighters</MenuItem>
            {accounts
              .filter((a) => a.type === 'FREIGHTER')
              .map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.businessName || a.id.substring(0, 8)}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Pickup from"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value)
              setPage(0)
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="Pickup to"
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setPage(0)
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            placeholder="Search id / address / driver"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Export CSV">
            <IconButton onClick={handleExport}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['admin-shipments'] })
              }
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard
            title="Failed to load shipments"
            message={(error as Error)?.message}
            onRetry={() => refetch()}
          />
        </Paper>
      ) : (
        <ShipmentRows
          rows={visibleRows}
          totalPages={totalPages}
          page={page}
          onPageChange={setPage}
          onSelect={(s) => {
            setSelected(s)
            setDetailOpen(true)
          }}
          accountById={accountById}
          loading={isLoading}
        />
      )}

      <ShipmentDetailDialog
        open={detailOpen}
        shipment={selected}
        onClose={() => {
          setDetailOpen(false)
          setSelected(null)
        }}
      />

    </Box>
  )
}

function PendingMatchingTab() {
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<ShipmentDto | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const toast = useToast()

  const queryClient = useQueryClient()

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-pending-matching', page],
    queryFn: () => shipmentService.getPendingMatching(page, PAGE_SIZE),
  })

  const triggerMatching = useMutation({
    mutationFn: async () => {
      const { cronJobService } = await import('../api/cronJobService')
      return cronJobService.triggerMatching()
    },
    onSuccess: () => toast.success('Matching triggered'),
    onError: (e) => toast.errorFrom(e, 'Failed to trigger matching'),
  })

  const triggerMatchingToday = useMutation({
    mutationFn: async () => {
      const { cronJobService } = await import('../api/cronJobService')
      return cronJobService.triggerMatchingToday()
    },
    onSuccess: () => toast.success("Today's matching triggered"),
    onError: (e) => toast.errorFrom(e, 'Failed to trigger matching'),
  })

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-filter'],
    queryFn: () => accountService.getAll(0, 200),
  })
  const accounts: AccountDto[] = useMemo(
    () => accountsData?.content ?? accountsData ?? [],
    [accountsData]
  )
  const accountById = useMemo(() => {
    const m = new Map<string, AccountDto>()
    accounts.forEach((a) => m.set(a.id, a))
    return m
  }, [accounts])

  const rows: ShipmentDto[] = data?.content ?? []
  const totalPages: number = data?.totalPages ?? 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} shipment(s) on this page waiting for matching
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayIcon />}
            onClick={() => triggerMatchingToday.mutate()}
            disabled={triggerMatchingToday.isPending}
          >
            Match Today
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<TrendingUpIcon />}
            onClick={() => triggerMatching.mutate()}
            disabled={triggerMatching.isPending}
          >
            Run Matching
          </Button>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['admin-pending-matching'] })
              }
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard
            title="Failed to load pending-matching shipments"
            message={(error as Error)?.message}
            onRetry={() => refetch()}
          />
        </Paper>
      ) : (
        <ShipmentRows
          rows={rows}
          totalPages={totalPages}
          page={page}
          onPageChange={setPage}
          onSelect={(s) => {
            setSelected(s)
            setDetailOpen(true)
          }}
          accountById={accountById}
          loading={isLoading}
        />
      )}

      <ShipmentDetailDialog
        open={detailOpen}
        shipment={selected}
        onClose={() => {
          setDetailOpen(false)
          setSelected(null)
        }}
      />

    </Box>
  )
}

export default function ShipmentsPage() {
  const [tab, setTab] = useState<TabKey>('statistics')

  return (
    <Box className="animate-fade-in" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Statistics" value="statistics" />
        <Tab label="Shipments" value="shipments" />
        <Tab label="Pending Matching" value="pending" />
      </Tabs>

      {tab === 'statistics' && <StatisticsTab />}
      {tab === 'shipments' && <ShipmentListTab />}
      {tab === 'pending' && <PendingMatchingTab />}
    </Box>
  )
}
